import { Component, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration } from 'chart.js';
import { Test } from 'src/app/models/test.model';
import { TestService } from 'src/app/services/test.service';
import { ReportService } from 'src/app/services/report.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AppConfig } from 'src/app/helpers/app.config';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import * as html2pdf from 'html2pdf.js';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
  selector: 'ct-mindset-report',
  templateUrl: './mindset-report.component.html',
  styleUrls: ['./mindset-report.component.scss']
})
export class MindsetReportComponent implements OnInit {

  public test: Test;
  public language: string;
  private languageId: number;
  public currentDate = new Date();
  public data: number[];
  public reportType: 'candidate' | 'consultant' | 'client';
  public xLabels;

  public barChartLabels = [
    '',
    '',
    '',
    '',
    ''
  ];
  public barChartPlugins = [DataLabelsPlugin, AnnotationPlugin];
  public barChartOptions: ChartConfiguration<'bar'>['options'];
  public barChartData: ChartConfiguration<'bar'>['data'];

  private computedBodyStyle = getComputedStyle(document.body)

  public colors = {
    primaryColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
    secondaryColor: this.computedBodyStyle.getPropertyValue('--color-secondary'),
    tertiaryColor: this.computedBodyStyle.getPropertyValue('--color-tertiary'),
    quaternaryColor: this.computedBodyStyle.getPropertyValue('--color-quaternary'),
    quinaryColor: this.computedBodyStyle.getPropertyValue('--color-quinary'),
    senaryColor: this.computedBodyStyle.getPropertyValue('--color-senary'),
    septenaryColor: this.computedBodyStyle.getPropertyValue('--color-septenary'),
    octonaryColor: this.computedBodyStyle.getPropertyValue('--color-octonary'),
    nonaryColor: this.computedBodyStyle.getPropertyValue('--color-nonary')
  }

  // corregido para ESFBITE-1342 por pentest para evitar XSS
  public get sanitizedCandidateName(): string {
    return (this.test?.candidate?.name || '').replace(/[<>"'&]/g, '');
  }

  constructor(
    private router: Router,
    public renderer: Renderer2,
    private translateService: TranslateService,
    private testService: TestService,
    private reportService: ReportService,
    private activatedRoute: ActivatedRoute,
    private utilsService: UtilsService,
    public loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.snapshot.queryParamMap.get('languageId')
      ? this.languageId = Number(this.activatedRoute.snapshot.queryParamMap.get('languageId'))
      : this.languageId = 1;
    this.language = this.utilsService.getLanguageFromLanguageId(this.languageId);
    this.translateService.use(this.language);
    this.translateService.setDefaultLang(this.language)
    document.documentElement.lang = this.language.split('-')[0];

    const token = this.activatedRoute.snapshot.paramMap.get('token');

    this.testService.getTestReportResults(token).subscribe((test: Test) => {
      this.test = test;

      // en el caso de tenerlo seteamos los valores del proyecto
      if (test.projectSelected && test.projectSelected.projectId) {

        this.colors = {
          primaryColor: this.test.projectSelected.projectPrimaryColor,
          secondaryColor: this.test.projectSelected.projectSecondaryColor,
          tertiaryColor: this.test.projectSelected.projectTertiaryColor,
          quaternaryColor: this.test.projectSelected.projectQuaternaryColor,
          quinaryColor: this.test.projectSelected.projectQuinaryColor,
          senaryColor: this.test.projectSelected.projectSenaryColor,
          septenaryColor: this.test.projectSelected.projectSeptenaryColor,
          octonaryColor: this.test.projectSelected.projectOctonaryColor,
          nonaryColor: this.test.projectSelected.projectNonaryColor
        }
        document.getElementById('reportModule').style.setProperty('--color-primary', this.colors.primaryColor);
        document.getElementById('reportModule').style.setProperty('--color-secondary', this.colors.secondaryColor);
        document.getElementById('reportModule').style.setProperty('--color-tertiary', this.colors.tertiaryColor);
        document.getElementById('reportModule').style.setProperty('--color-quaternary', this.colors.quaternaryColor);
        document.getElementById('reportModule').style.setProperty('--color-quinary', this.colors.quinaryColor);
        document.getElementById('reportModule').style.setProperty('--color-senary', this.colors.senaryColor);
        document.getElementById('reportModule').style.setProperty('--color-septenary', this.colors.septenaryColor);
        document.getElementById('reportModule').style.setProperty('--color-octonary', this.colors.octonaryColor);
        document.getElementById('reportModule').style.setProperty('--color-nonary', this.colors.nonaryColor);
      }

      switch (test.reportTypeId) {
        case '1': this.reportType = 'candidate'; break;
        case '2': this.reportType = 'consultant'; break;
        case '3': this.reportType = 'client'; break;
      }

      setTimeout(() => {
        if (this.reportType === 'candidate') {
          // Para poner la primera area en la misma pagina que el titulo fortalezas
          if (this.approvedAreasCount() > 0 && document.querySelector('#strengths .break')) {
            this.renderer.removeAttribute(document.querySelector('#strengths .break'), 'class');
          }
          if (this.approvedAreasCount() < 5 && document.querySelector('#tips .break')) {
            this.renderer.removeAttribute(document.querySelector('#tips .break'), 'class');
          }
        }
      }, 200);

      this.data = [
        test.result.informationDigitalLiteracy * 10,
        test.result.onlineCommunicationCollaboration * 10,
        test.result.digitalContentCreator * 10,
        test.result.networkSecurity * 10,
        test.result.issuesResolution * 10
      ];

      this.barChartData = {
        labels: this.barChartLabels,
        datasets: [{
          data: this.data,
          label: 'X.B.I',
          barThickness: 64,
          backgroundColor: [this.colors.primaryColor, this.colors.tertiaryColor, this.colors.senaryColor, this.colors.secondaryColor, this.colors.octonaryColor],
          hoverBackgroundColor: [this.colors.primaryColor, this.colors.tertiaryColor, this.colors.senaryColor, this.colors.secondaryColor, this.colors.octonaryColor],
          borderRadius: 5
        }]
      };

      this.translateService.getTranslation(this.language).subscribe(translations => {
        this.barChartOptions = {
          indexAxis: 'y',
          maintainAspectRatio: false,
          scales: {
            x: {
              grid: {
                drawBorder: false
              },
              position: 'top',
              min: 0,
              max: 100,
              ticks: {
                stepSize: 25,
                labelOffset: 80,
                maxRotation: 0,
                minRotation: 0,
                callback: (value, index, values) => this.xLabels[value]
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                padding: 54
              }
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
            datalabels: {
              anchor: 'end',
              align: 'start',
              color: (context) => {
                const colors = [
                  'white',
                  'white',
                  this.colors.primaryColor, // Azul
                  'white',
                  this.colors.primaryColor, // Amarillo
                ];
                return colors[context.dataIndex % colors.length]; // Asigna un color basado en el índice
              },
              font: {
                size: 14,
                family: 'Graphik'
              },
              formatter: (value, context) => {
                return translations.COMMON[this.level(this.data[context.dataIndex])]

              }
            },
            annotation: {
              common: {
                xScaleID: 'x',
                yScaleID: 'y',
                drawTime: 'afterDatasetsDraw'
              },
              annotations: {
                box1: {
                  type: 'box',
                  xMin: 57.3,
                  xMax: 58.3,
                  yMin: -0.4,
                  yMax: 0.4,
                  backgroundColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderRadius: 5
                },
                box2: {
                  type: 'box',
                  xMin: 52.9,
                  xMax: 53.9,
                  yMin: 0.6,
                  yMax: 1.4,
                  backgroundColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderRadius: 5
                },
                box3: {
                  type: 'box',
                  xMin: 53.6,
                  xMax: 54.6,
                  yMin: 1.6,
                  yMax: 2.4,
                  backgroundColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderRadius: 5
                },
                box4: {
                  type: 'box',
                  xMin: 55.0,
                  xMax: 56.0,
                  yMin: 2.6,
                  yMax: 3.4,
                  backgroundColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderRadius: 5
                },
                box5: {
                  type: 'box',
                  xMin: 57.5,
                  xMax: 58.5,
                  yMin: 3.6,
                  yMax: 4.4,
                  backgroundColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderColor: this.computedBodyStyle.getPropertyValue('--color-dark'),
                  borderRadius: 5
                }
              }
            }
          }
        };

        this.xLabels = {
          0: translations.COMMON.BEGINNER,
          25: translations.COMMON.BASIC,
          50: translations.COMMON.INTERMEDIATE,
          75: translations.COMMON.EXPERT
        };
      });
    });
  }

  public level(score: number): string {
    if (score <= 25) {
      return 'BEGINNER';
    } else if (score > 25 && score <= 50) {
      return 'BASIC';
    } else if (score > 50 && score <= 75) {
      return 'INTERMEDIATE';
    } else {
      return 'EXPERT';
    }
  }

  public approvedAreasCount(): number {
    let total = 0;
    if (this.test.result.informationDigitalLiteracy > 5) { total += 1; }
    if (this.test.result.onlineCommunicationCollaboration > 5) { total += 1; }
    if (this.test.result.digitalContentCreator > 5) { total += 1; }
    if (this.test.result.networkSecurity > 5) { total += 1; }
    if (this.test.result.issuesResolution > 5) { total += 1; }
    return total;
  }

  bowserprint() {
    const fileName = this.utilsService.reportFileName(this.test, this.language, this.reportType, true);
    const token = this.activatedRoute.snapshot.paramMap.get('token');

    this.reportService.getMDPdfReport(
      { reportMdToken: token, reportLanguageId: this.languageId }, fileName
    ).subscribe(pdf => {
      const downloadLink = document.createElement('a');
      downloadLink.href = pdf;
      downloadLink.download = '';
      downloadLink.click();
    }, error => this.utilsService.toast('error', 'Unexpected', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + 'external/md/report', body: { reportMdToken: token, reportLanguageId: this.languageId }, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } })));
  }

  print() {
    this.loadingService.startLoading();

    window.scrollTo(0, 0);
    this.renderer.setStyle(document.querySelector('body'), 'overflow', 'hidden');
    window.addEventListener('scroll', this.scrollToTop);

    document.querySelectorAll('#mindsetReport').forEach(element => this.renderer.setAttribute(element, 'class', 'print'));

    const fileName = this.utilsService.reportFileName(this.test, this.language, this.reportType, true);

    return new Promise<void>(resolve => setTimeout(() => {
      let worker = html2pdf().from(document.querySelectorAll('#mindsetReport')[0]).set({
        pagebreak: { before: '.page, .break', avoid: 'p' },
        // Reducir la escala en los informes mas largos
        html2canvas: { scale: (this.reportType !== 'candidate' || this.approvedAreasCount() > 2 ? 1.8 : 1.3) },
        image: { type: 'jpeg', quality: 0.8 },
        filename: fileName
      }).toPdf();

      const pages = [...Array.from(document.querySelectorAll('#mindsetReport'))];
      for (const [i, page] of pages.slice(1).entries()) {
        worker = worker.get('pdf').then(pdf => {
          pdf.addPage();
        }).from(page).toContainer().toCanvas().toPdf();
      }

      worker = worker.get('pdf').then(pdf => this.addHeaderAndFooter(pdf)).save().then(() => {
        this.renderer.setStyle(document.querySelector('body'), 'overflow', 'initial');
        window.removeEventListener('scroll', this.scrollToTop);
        document.querySelectorAll('#mindsetReport').forEach(element => this.renderer.removeAttribute(element, 'class'));
        resolve();
      }).get('pdf').then(pdf => {
        // Escribir el base64 del pdf en el html para obtenerlo desde bowserprint
        document.querySelector('#base64').textContent = pdf.output('datauristring').toString().split('base64,')[1];
        document.querySelector('#base64').classList.add('base64');
      });

      setTimeout(() => {
        this.loadingService.stopLoading();
      }, 1000);
    }, 500));
  }

  private scrollToTop() {
    window.scrollTo(0, 0);
  }

  private addHeaderAndFooter(pdf) {
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 2; i <= totalPages - 1; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(this.computedBodyStyle.getPropertyValue('--color-tertiary').trim());

      const relevateImg = new Image();
      relevateImg.src = 'assets/images/relevate-talent-assessment-logo.png';
      pdf.addImage(relevateImg, 'png', 15, 10, 55, 18);

      const randstadImg = new Image();
      randstadImg.src = 'assets/images/randstad-icon-pdf.png';
      pdf.addImage(randstadImg, 'png', 15, pdf.internal.pageSize.getHeight() - 19, 8, 4);

      pdf.text(
        (this.test.candidate.name
          ? this.utilsService.initials(this.test.candidate)
          : this.test.candidate.randstadCandidate) + ' | ' + (this.test.invitationId != 0 ? this.test.invitationId : this.test.externalId) + ' | | MD/01/SP', 25, pdf.internal.pageSize.getHeight() - 15);

      pdf.text((i).toString(), pdf.internal.pageSize.getWidth() - 15, pdf.internal.pageSize.getHeight() - 15);
    }
  }

}
